/** ***********************************************************************
 *
 * Copyright 2019 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ************************************************************************* */

import React, { Component } from 'react';
import { observer } from 'mobx-react';
import uuid from 'uuid';
import Busy from '../busy';
import { addClass, removeClass } from '../../utils/cssUtils';
import { getParam } from '../../utils/urlUtils';
import LoggerUtil from '../../utils/LoggerUtil';
import { PDPAnalytics } from '../../analytics';
import { APPS_COMPONENT_ID } from '../../utils/enums';
import { PDP_LAYOUTS } from '../../layouts/layoutConstants';
import { onBackClick } from '../../utils/pdpUtils/pdpEnums';
import './pdp.scss';

const COMPONENT_ID = 'PDPComponent';

class PDP extends Component {
    renderGuid = uuid.v4();

    ref = React.createRef();

    constructor(props) {
        super(props);
        this.state = {
            isReady: false, // False till correct PDP data not available.
        };
        this.awaitPDPData(); // Callback called again for a new app.
    }

    componentWillMount() {
        // Hide app-sidenav at screen size smaller than 960px
        requestAnimationFrame(() => {
            addClass(document.getElementsByClassName('app-sidenav')[0], 'hide-sidenav-pdp');
            addClass(document.querySelector('.app-sidenav-sm-trigger'), 'hide-sidenav-pdp');
        });
    }

    componentDidMount() {
        if (this.ref && this.ref.current) {
            this.ref.current.focus();
        }
    }

    componentDidUpdate(prevProps, prevState) {
        const { location: { pathname } } = this.props;
        const { isReady } = this.state;
        if (prevProps.location.pathname !== pathname) {
            this.awaitPDPData(); // Callback called again for a new app.
        }
        if (!prevState.isReady && isReady) {
            PDPAnalytics.pdpPageRenderAnalytic(this.renderGuid);
        }
    }

    componentWillUnmount() {
        removeClass(document.getElementsByClassName('app-sidenav')[0], 'hide-sidenav-pdp');
        removeClass(document.querySelector('.app-sidenav-sm-trigger'), 'hide-sidenav-pdp');
    }

    onBackButtonClick(e) {
        const { actionHandlerCallback } = this.PDPDataObject;
        onBackClick(e, actionHandlerCallback);
    }

    awaitPDPData() {
        const { isReady } = this.state;
        if (isReady) {
            this.setState({ isReady: false });
        }
        const {
            appConfig,
            routeParams: {
                productId,
                sectionId,
            },
            location: {
                search,
            },
        } = this.props;
        /*
         * getPDPDataCallback will return a Promise
         * which on being resolved will give the PDP data object
         */
        const version = getParam('version', search || '');
        const successCb = (PDPDataObject) => {
            this.PDPDataObject = PDPDataObject;
            /*
             * Initializing PDP Analytics object with App object.
             */
            PDPAnalytics.initAnalyticObject(PDPDataObject.app, sectionId);

            this.setState({ isReady: true });
        };
        const failureCb = (error) => {
            LoggerUtil.warn(`${APPS_COMPONENT_ID}: ${COMPONENT_ID}: AwaitPDPData: Failure callback: ${error}`);
            this.setState({ isReady: false });
        };
        appConfig.getPDPDataCallback(productId, version)
            .then(successCb, failureCb);
    }

    isPDPDataObjectValid() {
        const { routeParams: { productId } } = this.props;
        return this.PDPDataObject.app.productId === productId;
    }

    renderPDP() {
        const {
            appConfig: { layouts }, apps, appsPanelSkeleton, routeParams: {
                sectionId,
            },
        } = this.props;
        const { contentfulData: { layoutID } } = this.PDPDataObject;
        const PDPLayout = (layoutID && Object.prototype.hasOwnProperty.call(layouts.pdp, layoutID))
            ? layouts.pdp[layoutID] : layouts.pdp[PDP_LAYOUTS.DEFAULT];

        return (
            <PDPLayout
                PDPDataObject={this.PDPDataObject}
                layouts={layouts}
                appsList={apps.apps}
                appsPanelSkeleton={appsPanelSkeleton}
                sectionId={sectionId}
            />
        );
    }

    render() {
        const { isReady } = this.state;

        return (
            <div
                className="pdp-wrapper"
                ref={this.ref}
                tabIndex={-1}
                render-guid={this.renderGuid}
                onKeyDown={(e) => {
                    if (e.key === 'Esc' || e.key === 'Escape') {
                        this.onBackButtonClick(e);
                    }
                }}
            >
                {(!isReady || !this.isPDPDataObjectValid()) ? <Busy />
                    : this.renderPDP()
                }
            </div>
        );
    }
}
export default observer(PDP);
